import React from 'react';
import { styled } from '@mui/material/styles';

import UIKitChipBase, { UIKitChipSize } from './ChipBase';
import { colors } from '../../theme/tokens.colors';

export type UIKitRiskVariant = 'High' | 'Substantial' | 'Moderate' | 'Low';

export interface UIKitRiskChipProps {
  htmlId?: string;
  variant: UIKitRiskVariant;
  /** si es false, muestra solo las barras sin texto */
  withText?: boolean;
  size?: UIKitChipSize; // por si negocio después quiere small
  className?: string;
  style?: React.CSSProperties;
}

const StyledChipBase = styled(UIKitChipBase)({
  borderColor: `${colors.neutralVariant[300]} !important`,
  backgroundColor: `${colors.specialColors.white} !important`,
  color: `${colors.neutral[900]} !important`,
  boxShadow: 'none',
});

const Risk = styled('div')({
  display: 'flex',
});

const RiskBar = styled('div')({
  borderRadius: 2,
  width: 4,
  height: 12,
  margin: '0 0 0 2px',
  backgroundColor: colors.neutralVariant[200], // color "apagado" por default
});

// configuración de cada nivel de riesgo
const RISK_CONFIG: Record<UIKitRiskVariant, { label: string; color: string; filledBars: number }> =
  {
    High: {
      label: 'High',
      color: colors.error[600],
      filledBars: 4,
    },
    Substantial: {
      // si querés mantener el typo del Figma:
      label: 'Substancial',
      color: colors.error[500],
      filledBars: 3,
    },
    Moderate: {
      label: 'Moderate',
      color: colors.warning[500],
      filledBars: 2,
    },
    Low: {
      label: 'Low',
      color: colors.success[500],
      filledBars: 1,
    },
  };

const UIKitRiskChip: React.FC<UIKitRiskChipProps> = ({
  htmlId,
  className,
  style,
  withText = true,
  variant,
  size = 'medium',
}) => {
  const textInValues = Object.keys(RISK_CONFIG).includes(variant);
  if (!textInValues) {
    return <>N/A</>;
  }

  const config = RISK_CONFIG[variant as UIKitRiskVariant];

  const riskIcon = (
    <Risk>
      {Array.from({ length: 4 }).map((_, index) => {
        const isFilled = index < config.filledBars;
        // usa el neutro por default
        const barStyle: React.CSSProperties = isFilled ? { backgroundColor: config.color } : {};

        // index como key es seguro acá: siempre son exactamente 4 barras generadas
        // desde una longitud fija (Array.from({length: 4})), nunca reordenadas/
        // filtradas/agregadas dinámicamente.
        // eslint-disable-next-line react/no-array-index-key
        return <RiskBar key={index} style={barStyle} />;
      })}
    </Risk>
  );

  return (
    <StyledChipBase
      htmlId={htmlId}
      size={size}
      label={withText ? config.label : ''}
      iconLeft={riskIcon}
      className={className}
      style={style}
    />
  );
};

export default UIKitRiskChip;
